import { pgTable, text, varchar, integer, timestamp, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { sql } from "drizzle-orm";

// Negotiation bot — configurable strategy agent
export const bots = pgTable("bots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description").notNull(),
  strategy: text("strategy").notNull(), // system prompt / strategy description
  style: text("style").notNull().default("balanced"), // aggressive, cooperative, balanced, manipulative, analytical
  avatar: text("avatar").notNull().default("🤖"),
  llmProvider: text("llm_provider").notNull().default("anthropic"), // anthropic | yandex
  wins: integer("wins").notNull().default(0),
  losses: integer("losses").notNull().default(0),
  draws: integer("draws").notNull().default(0),
  rating: integer("rating").notNull().default(1000), // ELO rating
  club: text("club").notNull().default(""), // optional tag, e.g. negotiation club name
});

// Role pair within a case
export interface RolePair {
  id: string;
  roleA: string; // first participant's role
  roleB: string; // second participant's role
  goalA: string; // first participant's goal
  goalB: string; // second participant's goal
}

// Filter reference values
export const SCENARIO_TYPES = [
  "начальник-подчиненный",
  "изменение договоренностей",
  "увольнение",
  "продажи",
  "стратегическое партнерство",
] as const;

export const DOMAIN_TYPES = [
  "ИТ разработка",
  "семейные истории",
  "спорт и соревнования",
  "юристы и право",
] as const;

export const AUTHOR_COLLECTIONS = [
  "коллекция федерации",
  "коллекция томского клуба",
  "коллекция data-studio",
  "коллекция школы ринг",
] as const;

export const NEGOTIATION_TYPES = [
  "экспресс",
  "классика",
] as const;

// Negotiation case — situation with multiple possible role pairs
export const cases = pgTable("cases", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  description: text("description").notNull(), // situation description
  context: text("context").notNull(), // detailed context for bots
  author: text("author").notNull().default("Система"),
  scenarioType: text("scenario_type").notNull().default(""), // filter: scenario type
  domain: text("domain").notNull().default(""), // filter: subject domain
  authorCollection: text("author_collection").notNull().default(""), // filter: author collection
  negotiationType: text("negotiation_type").notNull().default("классика"), // filter: express or classic
  rolePairs: jsonb("role_pairs").$type<RolePair[]>().notNull().default([]),
  maxRounds: integer("max_rounds").notNull().default(8),
  difficulty: text("difficulty").notNull().default("medium"),
});

// Scenario — a resolved case + role pair selection (kept for duel reference)
export const scenarios = pgTable("scenarios", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  description: text("description").notNull(),
  context: text("context").notNull(),
  roleA: text("role_a").notNull(),
  roleB: text("role_b").notNull(),
  goalA: text("goal_a").notNull(),
  goalB: text("goal_b").notNull(),
  maxRounds: integer("max_rounds").notNull().default(8),
  difficulty: text("difficulty").notNull().default("medium"),
});

// Duel — a match between two bots or human vs bot
export const duels = pgTable("duels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  scenarioId: varchar("scenario_id").notNull(),
  botAId: varchar("bot_a_id").notNull(),
  botBId: varchar("bot_b_id"), // null if human vs bot
  mode: text("mode").notNull().default("bot_vs_bot"), // bot_vs_bot, human_vs_bot
  status: text("status").notNull().default("pending"), // pending, active, completed
  winnerId: varchar("winner_id"), // bot id or "human" or "draw"
  rounds: jsonb("rounds").$type<DuelRound[]>().notNull().default([]),
  scoreA: integer("score_a").notNull().default(0),
  scoreB: integer("score_b").notNull().default(0),
  judgeNotes: text("judge_notes"),
});

// Types for rounds
export interface DuelRound {
  round: number;
  speakerA: string; // message from participant A
  speakerB: string; // message from participant B
  analysisA?: string; // judge analysis of A's move
  analysisB?: string; // judge analysis of B's move
}

// Insert schemas
export const insertBotSchema = createInsertSchema(bots).omit({ id: true, wins: true, losses: true, draws: true, rating: true });
export const insertCaseSchema = createInsertSchema(cases).omit({ id: true });
export const insertScenarioSchema = createInsertSchema(scenarios).omit({ id: true });
export const insertDuelSchema = createInsertSchema(duels).omit({ id: true, status: true, winnerId: true, rounds: true, scoreA: true, scoreB: true, judgeNotes: true });

// Tournament match within a group
export interface TournamentMatch {
  id: string;
  participantAId: string; // bot id
  participantBId: string; // bot id
  caseId?: string; // assigned case for this match
  rolePairId?: string; // assigned role pair for this match
  duelId?: string; // reference to Duel when match is played
  winnerId?: string; // bot id or "draw"
  scoreA: number;
  scoreB: number;
  status: "pending" | "active" | "completed";
}

// Tournament group
export interface TournamentGroup {
  id: string;
  name: string;
  participantIds: string[]; // bot ids (up to 3)
  matches: TournamentMatch[];
}

/** Судья чемпионата с категорией (как на арене) */
export interface TournamentJudgeSlot {
  judgeId: string;
  category: string;
}

/** Экспресс: 5 судей — направляющие на переговоры */
export const TOURNAMENT_EXPRESS_JUDGE_SLOT_LABELS = [
  "Направляющие на переговоры",
  "Направляющие на переговоры",
  "Направляющие на переговоры",
  "Направляющие на переговоры",
  "Направляющие на переговоры",
] as const;

/**
 * Классика: 7 судей — доверяющие собственность; трое направляющих; устраивающиеся на работу;
 * плюс ещё двое направляющих (итого 7 мест).
 */
export const TOURNAMENT_CLASSIC_JUDGE_SLOT_LABELS = [
  "Доверяющие собственность",
  "Направляющие на переговоры",
  "Направляющие на переговоры",
  "Направляющие на переговоры",
  "Устраивающиеся на работу",
  "Направляющие на переговоры",
  "Направляющие на переговоры",
] as const;

// Tournament — championship with group stage
export const tournaments = pgTable("tournaments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  status: text("status").notNull().default("setup"), // setup, active, paused, completed
  /** express — один тайм; classic — два тайма со сменой сторон */
  duelFormat: text("duel_format").notNull().default("express"),
  judges: jsonb("judges").$type<TournamentJudgeSlot[]>().notNull().default([]),
  caseId: varchar("case_id"), // optional: which case to use
  rolePairId: varchar("role_pair_id"), // optional: which role pair
  groups: jsonb("groups").$type<TournamentGroup[]>().notNull().default([]),
  results: jsonb("results").$type<TournamentStanding[]>().notNull().default([]),
});

export interface TournamentStanding {
  botId: string;
  groupId: string;
  wins: number;
  losses: number;
  draws: number;
  points: number; // 3 for win, 1 for draw, 0 for loss
  goalsFor: number; // total score earned
  goalsAgainst: number; // total score conceded
}

export const insertTournamentSchema = createInsertSchema(tournaments).omit({ id: true, status: true, results: true });

// Types
export type Bot = typeof bots.$inferSelect;
export type InsertBot = z.infer<typeof insertBotSchema>;
export type NegotiationCase = typeof cases.$inferSelect;
export type InsertCase = z.infer<typeof insertCaseSchema>;
export type Scenario = typeof scenarios.$inferSelect;
export type InsertScenario = z.infer<typeof insertScenarioSchema>;
export type Duel = typeof duels.$inferSelect;
export type InsertDuel = z.infer<typeof insertDuelSchema>;
export type Tournament = typeof tournaments.$inferSelect;
export type InsertTournament = z.infer<typeof insertTournamentSchema>;
